import { IonContent, IonPage } from "@ionic/react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Button, IconButton } from "../components/Button";
import { Chip } from "../components/Chip";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { ProgressBar } from "../components/ProgressBar";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { WaterAddSheet, WaterBottlesSheet, WaterKindChips, WaterLimitSheet } from "../components/WaterSheets";
import { clockTime, dayLabel, todayStr } from "../domain/date";
import { foodOn } from "../domain/intake";
import type { WaterBottle, WaterKind, WaterSettings } from "../domain/types";
import {
  GLASS_ML,
  KIND_LABEL,
  drunkLitres,
  isOlder,
  lastSevenDays,
  litres,
  mlText,
  remindersSummary,
  waterAverage,
  waterEntries,
  waterKinds,
  waterSettings,
  waterTarget,
  waterTotal,
} from "../domain/water";
import { useGo } from "../lib/useGo";
import { useLogDrink } from "../lib/useLogDrink";
import { useKaya } from "../store/kayaStore";
import { removeWater, setHotDay, setWaterSettings } from "../store/toggles";
import "../components/Water.css";

/** At 60+ the page offers the two drinks a day is mostly made of, and nothing to configure. */
const CHAI_ML = 150;

type Open = { kind: "bottle"; bottle: WaterBottle } | { kind: "custom" } | { kind: "bottles" } | { kind: "limit" } | null;

const DIURETIC_OPTIONS: { value: NonNullable<WaterSettings["diuretic"]>; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];

/**
 * Food › Water (boards Water, WaterAdd, WaterParent; ADR-044).
 *
 * Answer first: what has been drunk today against the day's target, then the one place that says
 * how the target was worked out. Glass is the page's one primary; every other amount is a chip.
 * No streak, no colour for "goal met", no celebration — the Hero's bar is the only progress mark.
 */
export default function FoodWater() {
  const { S, update } = useKaya();
  const go = useGo();
  const logDrink = useLogDrink();
  const [open, setOpen] = useState<Open>(null);
  const [kind, setKind] = useState<WaterKind>("water");

  const ds = todayStr();
  const set = waterSettings(S);
  const t = waterTarget(S, ds);
  const entries = waterEntries(S, ds);
  const total = waterTotal(S.logs[ds]);
  const older = isOlder(S, ds);
  const kinds = waterKinds(S);
  const hot = !!S.logs[ds]?.hot;
  const sexKnown = S.profile.sex === "male" || S.profile.sex === "female";

  const back = <TopBar title="Water" back={{ to: "/food", label: "Back to Food" }} />;

  if (!foodOn(S))
    return (
      <IonPage>
        <IonContent>
          <Screen>
            {back}
            <div className="k-water">
              <Notice secondary={{ label: "Food settings", onClick: () => go("/you/food") }}>
                Water lives in Food, and food tracking is off.
              </Notice>
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );

  const add = (ml: number, k: WaterKind, note?: string) => logDrink(ds, { ml, kind: k, note });
  const saveSettings = (patch: Partial<Omit<WaterSettings, "at">>) => update((d) => setWaterSettings(d, patch));

  /* ---------- the answer ---------- */
  const goal = t?.ml ?? null;
  const left = goal !== null ? goal - total : null;
  const line =
    goal === null
      ? "Your doctor sets how much — add their limit below"
      : t?.ceiling
        ? `of your ${litres(goal)} L limit`
        : older || left === null
          ? `of ${litres(goal)} L today`
          : left > 0
            ? `of ${litres(goal)} L today · ${drunkLitres(left)} to go`
            : `of ${litres(goal)} L today · reached`;
  const aria =
    goal === null
      ? `${drunkLitres(total)} L drunk today`
      : `${drunkLitres(total)} L of ${litres(goal)} L drunk today${
          !older && !t?.ceiling && left !== null && left > 0 ? `, ${drunkLitres(left)} L to go` : ""
        }`;

  const last = entries[entries.length - 1];
  const week = older ? null : waterAverage(S, lastSevenDays(ds));

  return (
    <IonPage>
      <IonContent>
        <Screen>
          {back}
          <div className="k-water">
            <Hero
              ariaLabel="Drinks today"
              label={`Drinks today · ${dayLabel(ds)}`}
              figure={drunkLitres(total)}
              unit="L"
              line={line}
              visual={
                goal !== null ? (
                  <ProgressBar value={total} max={goal} ceiling={t?.ceiling} ariaLabel={aria} />
                ) : undefined
              }
            />

            {/* how the target was worked out — shown here and nowhere else */}
            {t?.kind === "doctor" ? (
              <Notice primary={{ label: "Add their limit", onClick: () => setOpen({ kind: "limit" }) }}>
                {t.text}
              </Notice>
            ) : t ? (
              <section className="k-group">
                <div className="k-rows">
                  <div className="k-water-evid">
                    <p className="k-water-evid__text">{t.text}</p>
                    {t.lines.length ? (
                      <dl className="k-water-evid__lines">
                        {t.lines.map((l) => (
                          <div key={l.label} className="k-water-evid__line">
                            <dt>{l.label}</dt>
                            <dd>{l.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                    <p className="k-water-evid__based">
                      <span>based on {t.basedOn}</span>
                      {t.est ? <Tag tone="est" /> : null}
                    </p>
                  </div>
                  {older && sexKnown && !t.ceiling ? (
                    <div className="k-water-ask">
                      <h2 className="k-water-ask__q" id="water-diuretic">
                        Is one of your BP tablets a water tablet (diuretic)?
                      </h2>
                      <Segmented<string>
                        ariaLabel="Is one of your BP tablets a water tablet?"
                        value={set.diuretic ?? ""}
                        options={DIURETIC_OPTIONS}
                        onChange={(v) => saveSettings({ diuretic: v as WaterSettings["diuretic"] })}
                      />
                      {t.caution ? <Notice tone="caution">{t.caution}</Notice> : null}
                    </div>
                  ) : null}
                </div>
              </section>
            ) : (
              <RowGroup>
                <NavRow
                  icon={I.user}
                  title="Add your weight"
                  subtitle="The target is 30 ml per kg of it"
                  to="/you/body"
                />
              </RowGroup>
            )}

            <div className="k-water-add">
              <h2 className="k-group__h">Add a drink</h2>
              <div className="k-water-chips">
                <Button
                  size="lg"
                  grow
                  icon={I.glass}
                  ariaLabel={`Add a glass, ${GLASS_ML} ml`}
                  onClick={() => add(GLASS_ML, older ? "water" : kind, "Glass")}
                >
                  Glass · {GLASS_ML} ml
                </Button>
              </div>
              {older ? (
                <div className="k-water-chips">
                  <Chip icon={I.plus} ariaLabel={`Add chai, ${CHAI_ML} ml`} onClick={() => add(CHAI_ML, "chai")}>
                    Chai · {CHAI_ML} ml
                  </Chip>
                </div>
              ) : (
                <>
                  <div className="k-water-chips" role="group" aria-label="Other amounts">
                    <Chip icon={I.plus} ariaLabel={`Add a sip, ${set.sip} ml`} onClick={() => add(set.sip, kind, "Sip")}>
                      Sip · {set.sip} ml
                    </Chip>
                    {set.bottles.map((b) => (
                      <Chip
                        key={b.id}
                        icon={I.bottle}
                        ariaLabel={`${b.name} bottle: choose how much`}
                        onClick={() => setOpen({ kind: "bottle", bottle: b })}
                      >
                        {b.name}
                        <span className="k-chip__more">{I["chevron-down"]}</span>
                      </Chip>
                    ))}
                    <Chip icon={I.edit} ariaLabel="Add a custom amount" onClick={() => setOpen({ kind: "custom" })}>
                      Custom
                    </Chip>
                  </div>
                  <WaterKindChips kinds={kinds} value={kind} onChange={setKind} labelId="water-kind" />
                  <p className="k-water-note">
                    Chai with sugar or milk? <Link to="/food">Log it in Food</Link> for its calories too.
                  </p>
                </>
              )}
            </div>

            {t?.additions ? (
              <Notice
                icon={I.sun}
                title="Hot day today — adds 500 ml"
                control={
                  <label className="k-row__switch">
                    <span className="tog">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={hot}
                        aria-label="Hot day today, adds 500 ml"
                        onChange={(e) => update((d) => setHotDay(d, ds, e.target.checked))}
                      />
                      <i />
                    </span>
                  </label>
                }
              >
                Kaya&rsquo;s own figure, not a guideline. Off until you turn it on.
              </Notice>
            ) : null}

            <section className="k-group" aria-label="Today's drinks">
              <h2 className="k-group__h">Today · {mlText(total)}</h2>
              <div className="k-rows">
                {entries.length ? (
                  entries.map((e) => {
                    const time = clockTime(e.at);
                    const name = KIND_LABEL[e.kind];
                    return (
                      <div key={e.id} className="k-water-entry">
                        <span className="k-water-entry__time">{time}</span>
                        <span className="k-water-entry__txt">
                          <span className="k-water-entry__kind">{name}</span>
                          {e.note && !older ? <span className="k-water-entry__sub">{e.note}</span> : null}
                        </span>
                        <span className="k-water-entry__ml">{mlText(e.ml)}</span>
                        <IconButton
                          icon={I.close}
                          label={`Remove ${name.toLowerCase()}, ${mlText(e.ml)} at ${time}`}
                          onClick={() => update((d) => removeWater(d, ds, e.id))}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="k-water-empty">Nothing logged yet today.</p>
                )}
                {last ? (
                  <div className="k-water-undo">
                    <Button variant="text" icon={I.undo} onClick={() => update((d) => removeWater(d, ds, last.id))}>
                      Undo last
                    </Button>
                    <span className="k-water-undo__what">
                      {clockTime(last.at)} · {KIND_LABEL[last.kind].toLowerCase()}, {mlText(last.ml)}
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            <RowGroup>
              <NavRow
                icon={I.bell}
                title="Reminders"
                subtitle={
                  older && set.remindOn
                    ? `${remindersSummary(S, ds)} · each says “time for a glass”`
                    : remindersSummary(S, ds)
                }
                to="/you/reminders/water"
              />
              {!older ? (
                <NavRow
                  icon={I.bottle}
                  title="Bottles and sip size"
                  subtitle={`${set.bottles.length ? `${set.bottles.length} bottle${set.bottles.length === 1 ? "" : "s"}` : "No bottles"} · sip ${set.sip} ml`}
                  onClick={() => setOpen({ kind: "bottles" })}
                />
              ) : null}
              <NavRow
                icon={I.info}
                title="A doctor's fluid limit"
                subtitle={set.limitMl ? `${litres(set.limitMl)} L a day, shown as a limit` : "None"}
                onClick={() => setOpen({ kind: "limit" })}
              />
            </RowGroup>

            <RowGroup>
              {!older ? (
                <NavRow
                  icon={I.progress}
                  title={
                    week
                      ? `7-day average · ${litres(week.drunkMl)} L${week.targetMl !== null ? ` of ${litres(week.targetMl)} L` : ""}`
                      : "7-day average"
                  }
                  subtitle={week ? `Logged on ${week.logged} of ${week.of} days` : "Nothing logged in the last 7 days"}
                  to="/progress/food"
                />
              ) : null}
              <NavRow icon={I.list} title="Every day's drinks" to="/progress/records/water" />
            </RowGroup>
          </div>
        </Screen>

        {open?.kind === "bottle" || open?.kind === "custom" ? (
          <WaterAddSheet
            bottle={open.kind === "bottle" ? open.bottle : null}
            kinds={kinds}
            kind={kind}
            dayLabel={dayLabel(ds)}
            onClose={() => setOpen(null)}
            onAdd={(a) => {
              logDrink(ds, a);
              setOpen(null);
            }}
          />
        ) : null}
        {open?.kind === "bottles" ? (
          <WaterBottlesSheet
            bottles={set.bottles}
            sip={set.sip}
            onClose={() => setOpen(null)}
            onSave={(v) => {
              saveSettings(v);
              setOpen(null);
            }}
          />
        ) : null}
        {open?.kind === "limit" ? (
          <WaterLimitSheet
            limitMl={set.limitMl}
            onClose={() => setOpen(null)}
            onSave={(ml) => {
              saveSettings({ limitMl: ml });
              setOpen(null);
            }}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
